import { useMemo } from 'react';
import { Target, Zap, Brain, TrendingUp } from 'lucide-react';
import { calculateDevelopmentProfile, determineArchetype, SKILL_CATEGORIES } from '@/utils/techDevCalculator';

/**
 * Player Development Profile Summary Component
 * Shows percentage distribution and 0-10 scores for each skill area
 */
const PlayerDevelopmentProfile = ({ techDev, showArchetype = true }) => {
  const profile = useMemo(() => calculateDevelopmentProfile(techDev), [techDev]);
  const archetype = useMemo(() => determineArchetype(profile), [profile]);
  
  // Don't show if no skills are evaluated
  if (profile.overall.evaluatedSkills === 0) {
    return null;
  }
  
  const areas = [
    {
      key: 'technical',
      name: 'Technical',
      icon: Target,
      data: profile.technical,
      ...SKILL_CATEGORIES.TECHNICAL
    },
    {
      key: 'physical',
      name: 'Physical',
      icon: Zap,
      data: profile.physical,
      ...SKILL_CATEGORIES.PHYSICAL
    },
    {
      key: 'tactical',
      name: 'Tactical / IQ',
      icon: Brain,
      data: profile.tactical,
      ...SKILL_CATEGORIES.TACTICAL
    }
  ];
  
  // Get color based on score (new thresholds: 8.1+ proficient, 6.1-8.0 developing, below 6.1 focus)
  const getScoreColor = (score) => {
    if (score >= 8.1) return 'text-green-400';
    if (score >= 6.1) return 'text-yellow-400';
    if (score > 0) return 'text-red-400';
    return 'text-muted-foreground';
  };
  
  // Get progress bar color
  const getProgressColor = (score) => {
    if (score >= 8.1) return 'bg-green-500';
    if (score >= 6.1) return 'bg-yellow-500';
    if (score > 0) return 'bg-red-500';
    return 'bg-muted';
  };

  return (
    <div className="mb-8" data-testid="player-development-profile">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <TrendingUp className="text-primary" size={22} />
          <h3 className="font-heading text-lg uppercase tracking-wider">Player Development Profile</h3>
        </div>
        <div className="text-sm text-muted-foreground">
          {profile.overall.evaluatedSkills}/{profile.overall.totalSkills} skills evaluated
        </div>
      </div>
      
      {/* Area Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {areas.map((area) => {
          const Icon = area.icon;
          const hasData = area.data.evaluatedCount > 0;
          
          return (
            <div 
              key={area.key}
              className={`bg-card border ${area.borderColor} rounded-sm p-4 transition-all`}
              data-testid={`profile-card-${area.key}`}
            >
              {/* Area Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className={area.color} size={18} />
                  <span className={`font-heading text-sm uppercase ${area.color}`}>
                    {area.name}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {area.data.evaluatedCount}/{area.data.totalCount}
                </span>
              </div>
              
              {/* Score */}
              <div className="mb-3">
                <div className="flex items-baseline gap-1">
                  <span className={`text-3xl font-heading ${hasData ? getScoreColor(area.data.score) : 'text-muted-foreground'}`}>
                    {hasData ? area.data.score.toFixed(1) : '-'}
                  </span>
                  <span className="text-sm text-muted-foreground">/10</span>
                </div>
                {/* Score Progress Bar */}
                <div className="h-1.5 bg-muted/30 rounded-full mt-2 overflow-hidden">
                  <div 
                    className={`h-full ${getProgressColor(area.data.score)} transition-all duration-500`}
                    style={{ width: `${hasData ? (area.data.score / 10) * 100 : 0}%` }}
                  />
                </div>
              </div>
              
              {/* Percentage */}
              <div className={`text-center py-2 rounded-sm ${area.bgColor}`}>
                <span className={`text-2xl font-heading ${hasData ? area.color : 'text-muted-foreground'}`}>
                  {hasData ? `${area.data.percentage}%` : '-'}
                </span>
                <p className="text-xs text-muted-foreground mt-1">of profile</p>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Overall Score & Archetype */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 bg-muted/20 border border-border/40 rounded-sm p-4">
        {/* Overall Score */}
        <div className="flex items-center gap-4 flex-1">
          <div className="text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Overall</p>
            <div className="flex items-baseline justify-center gap-1">
              <span className={`text-4xl font-heading ${getScoreColor(profile.overall.score)}`}>
                {profile.overall.score.toFixed(1)}
              </span>
              <span className="text-sm text-muted-foreground">/10</span>
            </div>
          </div>
          
          {/* Distribution Bar */}
          <div className="flex-1 hidden sm:block">
            <div className="h-3 rounded-full overflow-hidden flex">
              <div 
                className="bg-emerald-500 transition-all duration-500"
                style={{ width: `${profile.technical.percentage}%` }}
                title={`Technical: ${profile.technical.percentage}%`}
              />
              <div 
                className="bg-blue-500 transition-all duration-500"
                style={{ width: `${profile.physical.percentage}%` }}
                title={`Physical: ${profile.physical.percentage}%`}
              />
              <div 
                className="bg-yellow-500 transition-all duration-500"
                style={{ width: `${profile.tactical.percentage}%` }}
                title={`Tactical: ${profile.tactical.percentage}%`}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-muted-foreground">
              <span className="text-emerald-400">{profile.technical.percentage}%</span>
              <span className="text-blue-400">{profile.physical.percentage}%</span>
              <span className="text-yellow-400">{profile.tactical.percentage}%</span>
            </div>
          </div>
        </div>
        
        {/* Archetype */}
        {showArchetype && profile.overall.evaluatedSkills >= 5 && (
          <div className="border-t sm:border-t-0 sm:border-l border-border/40 pt-4 sm:pt-0 sm:pl-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Player Type</p>
            <div className="flex items-center gap-2">
              <span className="text-2xl">{archetype.icon}</span>
              <div>
                <p className="font-heading text-primary">{archetype.name}</p>
                <p className="text-xs text-muted-foreground">{archetype.description}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerDevelopmentProfile;
